/**
 * Checks if a user can manage (edit, toggle release) a folder
 */
export const canUserManageFolder = (user, folder, allFolders = []) => {
  if (!user || user.role === 'guest') return false;
  if (user.role === 'super_user') return true;

  const folderObj = typeof folder === 'string'
    ? allFolders.find(f => f._id === folder)
    : folder;

  if (!folderObj) return false;

  const creatorId = folderObj.createdBy?._id ?? folderObj.createdBy;
  const isCreator = creatorId?.toString() === user.id;

  if (isCreator) return true;

  // College admins can manage folders in their college
  if (user.role === 'college_admin') {
    const userColleges = user.colleges || [];
    if (folderObj.college && userColleges.includes(folderObj.college)) return true;
  }

  // Recursive check for parent ownership (optional, but keep for consistency)
  if (user.role === 'college_admin' || user.role === 'teacher') {
    const folderMap = new Map(allFolders.map(f => [f._id, f]));
    let currentParentId = folderObj.parent?._id ?? folderObj.parent;

    while (currentParentId) {
      const parentFolder = folderMap.get(currentParentId.toString());
      if (!parentFolder) break;

      const parentCreatorId = parentFolder.createdBy?._id ?? parentFolder.createdBy;
      if (parentCreatorId?.toString() === user.id) return true;

      currentParentId = parentFolder.parent?._id ?? parentFolder.parent;
    }
  }

  return false;
};

/**
 * Checks if a user can manage (edit, toggle release) a content item (Exam or Document)
 */
export const canUserManageContent = (user, item) => {
  if (!user || user.role === 'guest') return false;
  if (user.role === 'super_user') return true;

  const creatorId = item.createdBy?._id ?? item.createdBy;
  const isCreator = creatorId?.toString() === user.id;

  const authorizedEditors = item.authorizedEditors || [];
  const isAuthorized = authorizedEditors.some(id => (id._id ?? id).toString() === user.id);

  return isCreator || isAuthorized;
};

/**
 * Checks if a user can delete a content item
 */
export const canUserDeleteContent = (user, item) => {
  if (!user || user.role === 'guest') return false;
  if (user.role === 'super_user') return true;
  const creatorId = item.createdBy?._id ?? item.createdBy;
  return creatorId?.toString() === user.id;
};

/**
 * Checks if a user can create content in a specific folder
 */
export const canUserCreateInFolder = (user, folderId, allFolders = []) => {
  if (!user || user.role === 'guest') return false;
  if (user.role === 'super_user' || user.role === 'college_admin' || user.role === 'teacher') {
    if (!folderId) return user.role === 'super_user';
    return canUserManageFolder(user, folderId, allFolders);
  }
  return false;
};
